/**
 * Premium Selection math (OR-1241).
 *
 * Pure formulas behind the Premium Selection section. The section's
 * inputs are canonical numeric strings (same convention as the
 * Schedule editable cells: digits + optional 2-decimal cents, "" =
 * empty); the derived values are numbers, `null`-propagating like the
 * rest of `domain/pricing` — a missing operand nulls every value
 * downstream of it instead of rendering NaN.
 *
 * The async wrapper (mock latency today, the real recalc endpoint in
 * phase 2) lives in `src/api/endpoints/premium-selection.ts`. Keep
 * this file synchronous and side-effect free so it stays trivially
 * testable and can move server-side without changes.
 */

// ───────────────────────────── Types ──────────────────────────────

/** User-editable fields that participate in the premium walk. All
 *  canonical numeric strings ("" = not entered). Percent fields hold
 *  whole percents ("15" = 15%). */
export interface PremiumSelectionInputs {
    /** Selected retention level (dollar value, e.g. "100000"). */
    sir: string;
    /** Base rate credit, whole percent ("15" = 15%). The FIRST walk step
     *  (OR-1514), applied multiplicatively as (1 − rate). The backend seeds
     *  0.15 on a newly created quote, so a new submission loads with "15"
     *  here without the page ever sending it; quotes written before that
     *  change carry null and take no credit. Wire field is
     *  `base_rate_credit_factor` — NOT `base_rate_credit`. */
    baseRateCreditPct: string;
    /** Quality credit/debit, whole percent. "" = none selected. */
    qualityCreditPct: string;
    /** Experience credit/debit, whole percent ("10" = 10%). A RATE since
     *  OR-1540 (was a dollar amount subtracted from the walk); applied
     *  multiplicatively as (1 − rate) like its neighbours. "" = none entered,
     *  no default. Wire field is `experience_credit_factor`. */
    experienceCreditPct: string;
    /** Corridor credit, whole percent ("2" = 2%). A RATE since OR-1315
     *  (was a flat dollar credit); applied multiplicatively in the walk. */
    corridorCreditPct: string;
    // Additional coverages (dollar amounts).
    punitiveDamages: string;
    defenseInAddition: string;
    abuse: string;
    hnoa: string;
    // Commissions (whole percents).
    tricuraCommissionPct: string;
    agentCommissionPct: string;
    // Fees & premium (dollar amounts).
    selectedPremium: string;
    techRmFee: string;
    /** TRIA / Surplus Lines — manual whole percents (Andressa, jun-17:
     *  editable, shown as %). TRIA's $ contribution = Selected Premium ×
     *  `TRIA_PREMIUM_SHARE` (5%) × pct% — the Tech/RM Fee is NOT in the
     *  base (Henry, OR-1513). */
    triaPct: string;
    /** Surplus Lines Tax/Fee $ contribution = Selected Premium × pct% —
     *  strictly the selected premium, unlike TRIA; Tech/RM Fee is a
     *  Tricura-side add, not part of InsCipher's taxable premium. */
    surplusLinesTaxPct: string;
    surplusLinesFeePct: string;
    /** Stamping Fee $ contribution = taxable-base × pct% — same per-state
     *  base rule as Surplus Lines Tax/Fee (PM-confirmed: all three key off
     *  the same state's `hasTaxableFees`). */
    stampingFeePct: string;
    /** Extra fees (OR-1339) — flat dollar adds picked from the predefined
     *  list via the "+Add Fee" modal. "" = not added. Flow additively into
     *  Final Premium, same as the old always-visible fields these replace. */
    mcareFee: string;
    indianaPcfSurcharge: string;
    louisianaPcfSurcharge: string;
    // Policy terms (dollar amounts) — captured but not part of any
    // derived formula in the MVP walk.
    perOccurrenceLimit: string;
    policyAggregateLimit: string;
    /** Coverage B (General Liability) and Coverage C (Evacuation Expense)
     *  sub-limits, printed on the Policy Form's declarations page
     *  (OR-1455). Captured only — no formula reads them. Defaults are
     *  Andressa's mock values, still pending UW confirmation (her
     *  question 4: the old Limits of Liability modal had a single
     *  $25,000 "Evacuation expense" that the backend now splits in
     *  three). */
    fireDamagePerClaimLimit: string;
    fireDamageAggregateLimit: string;
    glEachClaimDeductible: string;
    medicalExpenseLimit: string;
    evacuationLimit: string;
    evacuationAggregateLimit: string;
    evacuationDeductible: string;
    /** Underwriter-selected loss-years window (bare integer count) — the
     *  full_loss_ratio denominator. "" = let the backend default it to the
     *  recorded-year span (backend PR #303). */
    utilizedLossYears: string;
}

/** Upstream facts + rate tables the formulas need. In phase 1 these
 *  come from a mock fixture; in phase 2 the backend recalc endpoint
 *  owns them (sums over Schedule, Loss Runs totals, state rates). */
export interface PremiumSelectionRefs {
    /** Σ maturity-adjusted premiums per facility (Schedule). */
    primaryPremium: number;
    /** Σ SIR-adjusted premiums per facility (Schedule). */
    retroDateCreditPremium: number;
    /** Total incurred losses (Loss Runs). */
    totalIncurredLosses: number;
    /** Loss-ratio denominator factor — TODO(confirm with Erin). */
    lossRatioFactor: number;
}

/** Every auto-calculated value on the page. `null` = not computable
 *  yet (an operand is missing). */
export interface PremiumSelectionDerived {
    primaryPremium: number | null;
    retroDateCreditPremium: number | null;
    /** Σ sir_adjusted_premium — "Premium w/ SIR Credit" (backend, OR-1119). */
    premiumWithSirCredit: number | null;
    adjustedPremium: number | null;
    totalPremium: number | null;
    /** System-suggested premium (premium at target loss ratio, backend). */
    premiumAtTargetLossRatio: number | null;
    totalCommissionPct: number | null;
    tria: number | null;
    surplusLinesTax: number | null;
    surplusLinesFee: number | null;
    stampingFee: number | null;
    finalPremium: number | null;
    /** 0–1 fraction. Loss ratio over the utilized-years window (renamed
     *  from selectedLossRatio, backend PR #303). */
    fullLossRatio: number | null;
    /** 0–1 fraction. Loss ratio over the most-recent 5 report years —
     *  backend-computed only; the legacy FE walk never derives it. */
    cappedLossRatio5yr: number | null;
    /** 0–1 fraction, signed. */
    deviationFromUnity: number | null;
}

// ──────────────────────────── Helpers ─────────────────────────────

/** Parse a canonical numeric string. "" / garbage → null. */
function num(raw: string): number | null {
    if (raw.trim() === "") return null;
    const n = Number(raw.replace(/,/g, ""));
    return Number.isFinite(n) ? n : null;
}

/** Like `num` but empty means "no amount entered yet" → 0, for
 *  additive terms that shouldn't null the whole formula. */
function amount(raw: string): number {
    return num(raw) ?? 0;
}

/**
 * Deviation from Unity — a FRONTEND-COMPUTED rate (the backend returns
 * `deviation_from_unity: null` by design and never recomputes it; see
 * orion-backend `docs/terminology.md`). Measures how far the underwriter's
 * Selected Premium sits above the `total_premium` floor:
 *   (selected_premium − total_premium) / total_premium
 * `0` = selected equals the floor; positive = above. `null` when Selected
 * Premium is absent or `total_premium` is missing/≤ 0.
 */
export function computeDeviationFromUnity(selectedPremiumInput: string, totalPremium: number | null): number | null {
    const selected = num(selectedPremiumInput);
    return selected === null || totalPremium === null || totalPremium <= 0 ? null : (selected - totalPremium) / totalPremium;
}

/**
 * Total Commission — a FRONTEND-COMPUTED rate (the backend returns
 * `total_commission_rate: null` and persists it as provided, same
 * contract as Deviation from Unity). Per the field tooltip:
 *   tricura commission + agent commission
 * `null` only when BOTH inputs are empty (one-sided sums pass through).
 */
export function computeTotalCommissionPct(tricuraPct: string, agentPct: string): number | null {
    const tricura = num(tricuraPct);
    const agent = num(agentPct);
    return tricura === null && agent === null ? null : (tricura ?? 0) + (agent ?? 0);
}

// ──────────────────────────── Fees walk ───────────────────────────

/** The predefined extra fees the "+Add Fee" modal offers (OR-1339). Custom
 *  (free-text) fee types are a later ticket — these three flat dollar adds
 *  are the whole catalog for now. */
export type ExtraFeeId = "mcareFee" | "indianaPcfSurcharge" | "louisianaPcfSurcharge";

export interface ExtraFeeType {
    id: ExtraFeeId;
    label: string;
}

export const EXTRA_FEE_TYPES: ExtraFeeType[] = [
    { id: "mcareFee", label: "MCare Fee" },
    { id: "indianaPcfSurcharge", label: "Indiana Patient Compensation Fund Surcharge" },
    { id: "louisianaPcfSurcharge", label: "Louisiana Patient Compensation Fund Surcharge" },
];

/** The predefined fee whose label the text names exactly (case-insensitive),
 *  or null when the text is a custom fee name (OR-1494). The "Select or type
 *  fee" field in the Fees modal routes a row by this: a match is one of the
 *  quote's fixed columns, anything else becomes a misc fee. */
export function resolveExtraFeeId(text: string): ExtraFeeId | null {
    const needle = text.trim().toLowerCase();
    if (!needle) return null;
    return EXTRA_FEE_TYPES.find((t) => t.label.toLowerCase() === needle)?.id ?? null;
}

/** The Fees & Premium block, $-resolved. `null` = not computable yet
 *  (no Selected Premium). */
export interface FeesBreakdown {
    tria: number | null;
    surplusLinesTax: number | null;
    surplusLinesFee: number | null;
    stampingFee: number | null;
    mcareFee: number;
    indianaPcfSurcharge: number;
    louisianaPcfSurcharge: number;
    /** Σ of the underwriter-defined misc fee amounts folded into Final
     *  Premium (OR-1494). Echoes the `miscFeesTotal` argument; null while
     *  the walk itself is not computable. */
    miscFees: number | null;
    finalPremium: number | null;
}

/**
 * Fees & Premium math (jun-25). FE-owned again: the backend returns
 * `final_premium` null, so the front computes it live off the inputs and
 * writes it back on save.
 *
 * Base = Selected Premium + Tech/RM Fee. TRIA is a whole-percent rate
 * applied to the TRIA-eligible share of the Selected Premium ALONE —
 * `TRIA_PREMIUM_SHARE` (5%) of it, per the pricing sheet (Henry, OR-1513:
 * "TRIA is (Selected Premium × 0.05) × TRIA Rate"; 265,000 × 0.05 × 0.03 =
 * 397.50). The Tech/RM Fee is not in that base. Surplus Lines Tax/Fee/Stamping Fee are
 * whole-percent rates applied to Selected Premium by default — but whether
 * Tech/RM Fee enters that base is a per-state InsCipher decision (OR-1440:
 * MO taxes Premium + Broker/Tech Fee; most states tax Premium alone), and
 * PM-confirmed all three fees share the SAME per-state rule, not independent
 * ones. `taxableBaseIncludesFee` lets the caller say which base applies. As
 * of OR-1440's `GET /v2/states/{code}` (`hasTaxableFees`), the caller can
 * look this up by the risk's home state instead of guessing — see `getState`
 * in `api/endpoints/states.ts` and its use in both `onCalculateTax` (keyed
 * off the state InsCipher just resolved) and the quote-load effect (keyed
 * off `tax_state`, the same state now persisted on the quote).
 * `inferTaxableBaseIncludesFee` below remains a fallback only for a quote
 * with no `tax_state` on record — one calculated before that column shipped,
 * or never calculated at all. Absent a signal, this falls back to Selected
 * Premium alone, same as before. The Mcare Fee and the Indiana/Louisiana PCF
 * surcharges (OR-1315 split of the old "Other" fee) are flat dollar adds:
 *
 *   final = base + selected·5%·TRIA% + taxBase·SLTax% + feeBase·SLFee% + stampBase·StampFee% + Mcare + PCFs + misc
 *
 * `misc` is the sum of the underwriter-defined misc fee lines (OR-1494),
 * passed in as `miscFeesTotal`: the documents already print those lines and
 * count them in Total Policy Cost (backend OR-1421), so leaving them out here
 * would make the on-screen Final Premium read lower than the quote — the
 * same gap the stamping fee had (OR-1477 / OR-1480).
 *
 * Null-propagates off Selected Premium (a missing premium ⇒ the card
 * renders "—"). `techRmFee` is the server-derived Gemini fee, as stored on the quote.
 */
export interface SurplusLinesOverrides {
    /**
     * InsCipher-authored amounts (OR-1440). When supplied they REPLACE the
     * rate-derived figures above and flow into Final Premium.
     *
     * InsCipher returns the amount and the rate independently, and they can
     * disagree by cents — backend PR #321 saw 84174.00 against a rate implying
     * 84174.22. It files the amount, so once it has calculated, that amount is
     * the number of record and recomputing `base × rate` would drift from the
     * filing. Left undefined for a manually-entered rate, where there is no
     * authoritative amount and the rate is all we have.
     */
    surplusLinesTax?: number | null;
    surplusLinesFee?: number | null;
    /** Stamping Fee counterpart — same contract as the two above. */
    stampingFee?: number | null;
}

/** Whether InsCipher's taxable base for Surplus Lines Tax / Fee / Stamping
 *  Fee includes the Tech/RM Fee, per field — looked up via
 *  `GET /v2/states/{code}`'s `hasTaxableFees` where a home state is
 *  available, else inferred as a fallback (see `inferTaxableBaseIncludesFee`
 *  and `inferCombinedTaxableBaseIncludesFee`). In practice callers set all
 *  three fields to the SAME combined value (PM-confirmed: all three key off
 *  one per-state rule); kept as separate fields rather than one bare boolean
 *  so `computeFees` doesn't have to assume that holds. Omitted/`undefined`
 *  fields default to Selected Premium alone, same as before this base became
 *  state-dependent. */
/** Share of the Selected Premium that TRIA rates apply to. The pricing
 *  sheet's TRIA line is `Selected Premium × 5% × TRIA rate` (Henry,
 *  OR-1513); before that the FE charged the rate on the full Selected
 *  Premium + Tech/RM Fee, 20× the sheet's figure. */
export const TRIA_PREMIUM_SHARE = 0.05;

export interface TaxableBaseIncludesFee {
    tax?: boolean;
    fee?: boolean;
    stamping?: boolean;
}

export function computeFees(
    inputs: PremiumSelectionInputs,
    techRmFee: number,
    overrides?: SurplusLinesOverrides,
    taxableBaseIncludesFee?: TaxableBaseIncludesFee,
    /** Σ misc fee amounts (OR-1494). Defaults to 0 so callers without fee
     *  lines are unchanged. */
    miscFeesTotal = 0,
): FeesBreakdown {
    const mcareFee = amount(inputs.mcareFee);
    const indianaPcfSurcharge = amount(inputs.indianaPcfSurcharge);
    const louisianaPcfSurcharge = amount(inputs.louisianaPcfSurcharge);
    const selected = num(inputs.selectedPremium);
    if (selected === null) {
        return {
            tria: null,
            surplusLinesTax: null,
            surplusLinesFee: null,
            stampingFee: null,
            mcareFee,
            indianaPcfSurcharge,
            louisianaPcfSurcharge,
            miscFees: null,
            finalPremium: null,
        };
    }
    const miscFees = Number.isFinite(miscFeesTotal) ? miscFeesTotal : 0;
    const base = selected + techRmFee;
    // TRIA: Selected Premium × 5% × rate — no Tech/RM Fee in the base (OR-1513).
    const tria = selected * TRIA_PREMIUM_SHARE * (amount(inputs.triaPct) / 100);
    // `?? computed` rather than a truthiness check: a legitimate 0.00 from
    // InsCipher (e.g. TX, where the service charge is inactive) must win over
    // the rate-derived value, not fall through it.
    //
    // Surplus Lines Tax/Fee/Stamping Fee apply to Selected Premium by
    // default; `base` is only used per field when the caller has established
    // (via `inferTaxableBaseIncludesFee` or the real per-state lookup) that
    // InsCipher's own basis for this quote includes the Tech/RM Fee — a
    // per-state decision shared by all three fees.
    const taxBase = taxableBaseIncludesFee?.tax ? base : selected;
    const feeBase = taxableBaseIncludesFee?.fee ? base : selected;
    const stampingBase = taxableBaseIncludesFee?.stamping ? base : selected;
    const surplusLinesTax =
        overrides?.surplusLinesTax ?? taxBase * (amount(inputs.surplusLinesTaxPct) / 100);
    const surplusLinesFee =
        overrides?.surplusLinesFee ?? feeBase * (amount(inputs.surplusLinesFeePct) / 100);
    const stampingFee =
        overrides?.stampingFee ?? stampingBase * (amount(inputs.stampingFeePct) / 100);
    const finalPremium =
        base + tria + surplusLinesTax + surplusLinesFee + stampingFee + mcareFee + indianaPcfSurcharge + louisianaPcfSurcharge + miscFees;
    return {
        tria,
        surplusLinesTax,
        surplusLinesFee,
        stampingFee,
        mcareFee,
        indianaPcfSurcharge,
        louisianaPcfSurcharge,
        miscFees,
        finalPremium,
    };
}

/**
 * Infers whether InsCipher's last-filed surplus-lines amount included the
 * Tech/RM Fee in its taxable base (state-specific — e.g. MO taxes Premium +
 * Broker/Tech Fee, most states tax Premium alone).
 *
 * Superseded by `GET /v2/states/{code}`'s `hasTaxableFees` (OR-1440) at both
 * call sites that have a home state to look it up with: `onCalculateTax`
 * (the state InsCipher just resolved) and the quote-load effect (`tax_state`,
 * the same state now persisted on the quote). Kept as a fallback only for a
 * quote with no `tax_state` on record — calculated before that column
 * shipped, or never calculated at all — where this reverse-solves the base
 * InsCipher actually used from the amount + rate it filed:
 *
 *   impliedBase = filedAmount / (rate / 100)
 *
 * and checks which of the two candidate bases (Selected Premium alone, or
 * Selected Premium + Tech/RM Fee) it's closer to, tolerating the cent-level
 * drift InsCipher's amount and rate can have (see `SurplusLinesOverrides`).
 *
 * Returns `null` when there's nothing to infer from — no filed amount, a
 * filed amount of exactly 0 (a $0 charge divides out to an implied base of
 * 0 regardless of rate, which is uninformative rather than "premium alone";
 * this is the common case for the Surplus Lines FEE, inactive/$0 in most
 * states per InsCipher — see `inferCombinedTaxableBaseIncludesFee`), a zero
 * rate, or a zero Tech/RM Fee (the two candidate bases coincide). Callers
 * should keep whatever flag they already had in that case, not treat `null`
 * as "premium alone".
 */
export function inferTaxableBaseIncludesFee(
    filedAmount: number | null,
    ratePct: string,
    selectedPremium: string,
    techRmFee: number,
): boolean | null {
    const rate = num(ratePct);
    const selected = num(selectedPremium);
    if (filedAmount === null || filedAmount === 0 || rate === null || rate === 0 || selected === null || techRmFee === 0) {
        return null;
    }
    const impliedBase = filedAmount / (rate / 100);
    const premiumOnly = selected;
    const premiumPlusFee = selected + techRmFee;
    return Math.abs(impliedBase - premiumPlusFee) < Math.abs(impliedBase - premiumOnly);
}

/**
 * Combines the tax and fee signals from `inferTaxableBaseIncludesFee` into
 * ONE taxable-base decision for the quote.
 *
 * Surplus Lines Tax and the Surplus Lines Fee (service charge) are both a
 * percentage of the SAME state-defined taxable premium — the original OR
 * ticket describes a single "SL Tax Applies To: Premium + Broker Fee +
 * Carrier Fee" rule per state, not separate rules for tax vs. fee. So they
 * should share one answer rather than being inferred independently.
 *
 * That matters in practice because the two signals are not equally
 * reliable: the fee/service-charge is $0 in most states (InsCipher), which
 * makes `feeSignal` `null` (per the amount-of-0 case above) far more often
 * than `taxSignal` — a state that levies surplus lines tax at all almost
 * always has a nonzero rate. Preferring `taxSignal` means the fee still
 * gets the correct base even when its own amount gives no signal; falling
 * back to `feeSignal` covers the rarer reverse case.
 */
export function inferCombinedTaxableBaseIncludesFee(taxSignal: boolean | null, feeSignal: boolean | null): boolean {
    return taxSignal ?? feeSignal ?? false;
}

/** Final Premium only — the value the summary cards + Fees row show and
 *  the quote PUT writes to `final_premium`. */
export function computeFinalPremium(inputs: PremiumSelectionInputs, techRmFee: number): number | null {
    return computeFees(inputs, techRmFee).finalPremium;
}

// ────────────────────── Surplus-lines provenance ──────────────────────

/** Inputs to `resolveSurplusLinesProvenance` — tax, fee, and stamping are
 *  judged independently, since the underwriter may override only one field. */
export interface SurplusLinesProvenanceInputs {
    /** Live `%` input differs from the last-loaded/calculated rate this
     *  session (the original OR-1440 heuristic). */
    taxRateOverriddenLocally: boolean;
    feeRateOverriddenLocally: boolean;
    stampingRateOverriddenLocally: boolean;
    /** Backend's own provenance flag, when the environment ships it.
     *  `null` = the backend hasn't shipped the column — never `false`. */
    taxManuallySetByServer: boolean | null;
    feeManuallySetByServer: boolean | null;
    stampingManuallySetByServer: boolean | null;
}

export interface SurplusLinesProvenance {
    taxNotInsCipherAuthored: boolean;
    feeNotInsCipherAuthored: boolean;
    stampingNotInsCipherAuthored: boolean;
}

/**
 * Whether the persisted surplus-lines $ amount can still be trusted, per
 * field (OR-1440 follow-up).
 *
 * NOT InsCipher-authored if EITHER: (a) the live session already knows the
 * rate was hand-typed, OR (b) the backend itself says so. (b) catches the
 * case (a) can't: after a reload, the live input is hydrated from the
 * already-overridden persisted rate, so it no longer differs from the
 * reloaded baseline and (a) reads false — even though that persisted rate
 * was itself a manual override the backend may never have recomputed the $
 * amount for.
 *
 * This is an OR, not a `??` preference chain: a `??` chain would let a
 * *stale* backend `false` (read before the user's current, unsaved edit)
 * wrongly override this session's live override. OR is safe because the
 * local-session signal always wins when it fires, and the backend flag is
 * only consulted for the remaining case (rate unchanged this session).
 * Falls back to (a) alone whenever the backend hasn't shipped the flag
 * (stays `null`) — no regression on environments without it.
 */
export function resolveSurplusLinesProvenance(inputs: SurplusLinesProvenanceInputs): SurplusLinesProvenance {
    return {
        taxNotInsCipherAuthored: inputs.taxRateOverriddenLocally || inputs.taxManuallySetByServer === true,
        feeNotInsCipherAuthored: inputs.feeRateOverriddenLocally || inputs.feeManuallySetByServer === true,
        stampingNotInsCipherAuthored: inputs.stampingRateOverriddenLocally || inputs.stampingManuallySetByServer === true,
    };
}

// ───────────────────────────── Derive ─────────────────────────────

export function derivePremiumSelection(inputs: PremiumSelectionInputs, refs: PremiumSelectionRefs): PremiumSelectionDerived {
    const retro = refs.retroDateCreditPremium;

    // Premium walk, all four steps in the order the pricing model's Premium
    // sheet applies them (OR-1514): base rate, quality, experience and
    // corridor are all rates applied multiplicatively (experience since
    // OR-1540). The live value is backend-owned (`adjusted_premium` off the
    // quote wire, base premium_with_sir_credit); this legacy derive mirrors
    // the shape off the retro base for tests. No floor: every step is bounded
    // [0,1], so the product can only approach zero from above.
    const qualityPct = num(inputs.qualityCreditPct);
    const adjustedPremium =
        retro *
        (1 - amount(inputs.baseRateCreditPct) / 100) *
        (1 - (qualityPct ?? 0) / 100) *
        (1 - amount(inputs.experienceCreditPct) / 100) *
        (1 - amount(inputs.corridorCreditPct) / 100);

    const totalPremium =
        adjustedPremium + amount(inputs.punitiveDamages) + amount(inputs.defenseInAddition) + amount(inputs.abuse) + amount(inputs.hnoa);

    const totalCommissionPct = computeTotalCommissionPct(inputs.tricuraCommissionPct, inputs.agentCommissionPct);

    // Fees block — TRIA applies to Selected Premium × 5%; Surplus Lines
    // Tax/Fee apply to Selected Premium alone — folded into Final Premium
    // by `computeFees`. Empty % ⇒ $0 contribution.
    const selected = num(inputs.selectedPremium);
    const { tria, surplusLinesTax, surplusLinesFee, stampingFee, finalPremium } = computeFees(inputs, amount(inputs.techRmFee));

    const fullLossRatio =
        selected === null || selected <= 0 || refs.lossRatioFactor <= 0
            ? null
            : refs.totalIncurredLosses / (selected * refs.lossRatioFactor);

    const deviationFromUnity = computeDeviationFromUnity(inputs.selectedPremium, totalPremium);

    return {
        primaryPremium: refs.primaryPremium,
        retroDateCreditPremium: retro,
        // Legacy FE-derive path (test-only); the live value comes from the
        // quote wire (`premium_with_sir_credit`) via derivedFromWireQuote.
        premiumWithSirCredit: null,
        adjustedPremium,
        totalPremium,
        // Backend-only metric — no FE derivation exists for the suggested premium.
        premiumAtTargetLossRatio: null,
        totalCommissionPct,
        tria,
        surplusLinesTax,
        surplusLinesFee,
        stampingFee,
        finalPremium,
        fullLossRatio,
        // Backend-only metric — no FE derivation exists for the 5-year cap.
        cappedLossRatio5yr: null,
        deviationFromUnity,
    };
}

// ─────────────────────────── Defaults ─────────────────────────────

/** Field defaults per the Figma spec: commissions 15/10, target loss
 *  ratio 50, Accelerant TLR 54, corridor/tech-RM $0, policy limits
 *  $1M / $3M. Everything else starts empty. */
export function defaultPremiumSelectionInputs(): PremiumSelectionInputs {
    return {
        sir: "",
        // Left EMPTY on purpose, not "15": the 0.15 is the backend's to seed
        // (it does so only when the quote row is first created), and hardcoding
        // it here would show a credit on quotes that predate OR-1514 and carry
        // null. The value always arrives via the GET.
        baseRateCreditPct: "",
        qualityCreditPct: "",
        experienceCreditPct: "",
        corridorCreditPct: "0",
        punitiveDamages: "",
        defenseInAddition: "",
        abuse: "",
        hnoa: "",
        tricuraCommissionPct: "15",
        agentCommissionPct: "10",
        selectedPremium: "",
        techRmFee: "0",
        triaPct: "",
        surplusLinesTaxPct: "",
        surplusLinesFeePct: "",
        stampingFeePct: "",
        mcareFee: "",
        indianaPcfSurcharge: "",
        louisianaPcfSurcharge: "",
        perOccurrenceLimit: "1000000",
        policyAggregateLimit: "3000000",
        // Coverage B / C dec sub-limits (OR-1455) — Andressa's mock values.
        // Sin default: el mock de Andressa nunca mostró este campo, así que no
        // hay cifra con fuente. Vacío hasta que UW confirme (su pregunta 4).
        fireDamagePerClaimLimit: "",
        fireDamageAggregateLimit: "100000",
        glEachClaimDeductible: "5000",
        medicalExpenseLimit: "5000",
        evacuationLimit: "25000",
        evacuationAggregateLimit: "25000",
        evacuationDeductible: "25000",
        utilizedLossYears: "",
    };
}

// ───────────────────── Tech/RM Fee validation ──────────────────────

/**
 * Per-facility Gemini Tech/RM rate the backend stores manual fees against
 * (backend PR #371). The backend derives the fee as `covered facilities × this`
 * while `tech_rm_fee_manually_set` is false; the frontend never derives it
 * (OR-1501). This copy exists only to validate a typed fee below.
 */
export const TECH_RM_FEE_PER_FACILITY = 5000;

/**
 * Whether a typed Tech/RM Fee is one the backend stores as typed (OR-1700).
 *
 * The backend keeps a manual fee as whole facilities × the per-facility rate
 * and normalises anything off that boundary (`tech_rm_fee_recalculated`:
 * $32,000 → $30,000, $500 → $0). The input never rounds on the user's behalf,
 * so an off-boundary amount is rejected instead and never reaches a save.
 * 0 is valid: it is how the fee is removed (OR-1701).
 *
 * Canonical input string in (no separators); "" is not a fee and is invalid.
 */
export function isValidTechRmFee(raw: string): boolean {
    if (raw.trim() === "") return false;
    const n = Number(raw.replace(/,/g, ""));
    return Number.isFinite(n) && n >= 0 && n % TECH_RM_FEE_PER_FACILITY === 0;
}
